// A held (or posted) adjustment request, in words (spec 06 §4.11 and §4.12).
//
// §4.11's first summary printed "Adjustment · 50 kg · Found · note". It named no
// product, no batch, no place and no direction, so an approver could not tell
// a gain from a loss before approving it. This reads the §2.12 payload and says
// what will happen: which product, which batch, where, which way, and what it
// is booked against. The direction is the side that is physical stock (a bin,
// a zone, a quarantine cage…); the other side is found, work in progress or
// scrap (spec 06 §3.4 step 7's convention).
//
// `movement_payload` is `unknown` jsonb on the wire, so every field is read
// defensively and a payload this screen cannot read says so.

import { enumLabel } from "../../../../lib/enum-label";
import { useMemo } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  useLocationPathIndex,
  useLotIndex,
  useProductIndex,
  type InventoryLocation,
} from "../hooks/inventory-queries";
import {
  counterOfKind,
  directionOfKinds,
  readHeldPayload,
  type AdjustCounter,
  type AdjustDirection,
  type HeldPayload,
} from "../lib/adjust";
import { formatMeasure, shortId } from "../lib/measure";

export interface PayloadRefs {
  readonly productLabel: (id: string | undefined) => string | undefined;
  readonly lotCode: (id: string | undefined) => string | undefined;
  readonly location: (id: string | undefined) => InventoryLocation | undefined;
  readonly isPending: boolean;
}

/** Resolve every product, batch and location a page of payloads names, in one
 * batch each — the ids on THIS page, never a walk. */
export function usePayloadRefs(payloads: readonly unknown[]): PayloadRefs {
  const held = useMemo(() => payloads.map((payload) => readHeldPayload(payload)), [payloads]);
  const productIds = held.flatMap((p) => (p?.productId !== undefined ? [p.productId] : []));
  const lotIds = held.flatMap((p) => (p?.lotId !== undefined ? [p.lotId] : []));
  const locationIds = held.flatMap((p) => [
    ...(p?.fromLocationId !== undefined ? [p.fromLocationId] : []),
    ...(p?.toLocationId !== undefined ? [p.toLocationId] : []),
  ]);
  const products = useProductIndex(productIds);
  const lots = useLotIndex(lotIds);
  const locations = useLocationPathIndex(locationIds);
  return {
    productLabel: (id) => {
      if (id === undefined) return undefined;
      const product = products.byId.get(id);
      return product === undefined ? undefined : `${product.sku} · ${product.name}`;
    },
    lotCode: (id) => (id === undefined ? undefined : lots.byId.get(id)?.lot_code),
    location: (id) => (id === undefined ? undefined : locations.byId.get(id)),
    isPending: products.isPending || lots.isPending || locations.isPending,
  };
}

export interface HeldWords {
  readonly sentence: string;
  readonly direction: AdjustDirection | "relocation" | undefined;
  readonly counter: AdjustCounter | undefined;
  /** The physical side, when there is exactly one. */
  readonly stockLocationId: string | undefined;
  readonly payload: HeldPayload;
}

/**
 * The payload as one sentence in the reader's language. Pure, so the words an
 * approver reads are unit-tested without rendering. An id that has not
 * resolved (still loading, deleted, or outside the reader's scope) renders as
 * its short id rather than as a blank.
 */
export function heldWords(
  t: TFunction,
  lotTerm: string,
  locale: string,
  raw: unknown,
  refs: PayloadRefs,
): HeldWords | undefined {
  const payload = readHeldPayload(raw);
  if (payload?.movementType === undefined) return undefined;
  const from = refs.location(payload.fromLocationId);
  const to = refs.location(payload.toLocationId);
  const direction = directionOfKinds(from?.kind, to?.kind);
  const placeOf = (id: string | undefined, loc: InventoryLocation | undefined): string =>
    loc?.path ?? (id === undefined ? "—" : shortId(id));
  const product =
    refs.productLabel(payload.productId) ??
    (payload.productId === undefined ? "—" : shortId(payload.productId));
  const lotCode =
    refs.lotCode(payload.lotId) ??
    (payload.lotId === undefined ? undefined : shortId(payload.lotId));
  const lotPart =
    lotCode === undefined
      ? ""
      : t("inventory.adjustRequest.lotPart", { lot: lotTerm, code: lotCode });
  const qty = formatMeasure(payload.quantity ?? "", { locale });
  const uom = payload.uom ?? "";
  const counterKind =
    direction === "increase" ? from?.kind : direction === "decrease" ? to?.kind : undefined;
  const counter = counterOfKind(counterKind);
  const counterText =
    counter !== undefined
      ? t(`inventory.adjust.counterShort.${counter}`)
      : placeOf(
          direction === "increase" ? payload.fromLocationId : payload.toLocationId,
          direction === "increase" ? from : to,
        );
  let sentence: string;
  if (payload.movementType === "adjustment" && direction === "increase") {
    sentence = t("inventory.adjustRequest.summary.increase", {
      qty,
      uom,
      product,
      lotPart,
      place: placeOf(payload.toLocationId, to),
      counter: counterText,
    });
  } else if (
    // D-328: a held manual scrap reads as the write-off it is.
    (payload.movementType === "adjustment" || payload.movementType === "scrap") &&
    direction === "decrease"
  ) {
    sentence = t("inventory.adjustRequest.summary.decrease", {
      qty,
      uom,
      product,
      lotPart,
      place: placeOf(payload.fromLocationId, from),
      counter: counterText,
    });
  } else if (payload.movementType === "adjustment" && direction === "relocation") {
    sentence = t("inventory.adjustRequest.summary.relocation", {
      qty,
      uom,
      product,
      lotPart,
      from: placeOf(payload.fromLocationId, from),
      to: placeOf(payload.toLocationId, to),
    });
  } else {
    sentence = t("inventory.adjustRequest.summary.other", {
      type: enumLabel(t, "inventory.vocab.movement", payload.movementType),
      qty,
      uom,
      product,
      lotPart,
      from: placeOf(payload.fromLocationId, from),
      to: placeOf(payload.toLocationId, to),
    });
  }
  return {
    sentence,
    direction,
    counter,
    stockLocationId:
      direction === "increase"
        ? payload.toLocationId
        : direction === "decrease"
          ? payload.fromLocationId
          : undefined,
    payload,
  };
}

/** The held movement, its reason and the requester's note. */
export function HeldAdjustment({
  payload,
  refs,
  withNote = true,
  testId,
}: {
  payload: unknown;
  refs: PayloadRefs;
  /** `true` prints the note after the reason; `"line"` puts it on a line of
   * its own, clipped to one line with the whole note on hover (the approvals
   * list, where Approve sits on the row). */
  withNote?: boolean | "line";
  testId?: string;
}) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const words = heldWords(t, term("lot"), i18n.language, payload, refs);
  if (words === undefined) {
    return (
      <span className="inv-muted" data-testid={testId}>
        {t("inventory.approvals.payloadUnreadable")}
      </span>
    );
  }
  const reason = words.payload.reason;
  const note = words.payload.note;
  return (
    <span
      className="payload-summary"
      data-testid={testId}
      data-direction={words.direction ?? "unknown"}
      data-counter={words.counter ?? "none"}
    >
      <span className="payload-summary-sentence">{words.sentence}</span>
      {reason !== undefined ? (
        <span className="inv-hint">
          {" · "}
          {enumLabel(t, "inventory.vocab.reason", reason)}
        </span>
      ) : null}
      {withNote === true && note !== undefined ? (
        <span className="inv-hint">{` · “${note}”`}</span>
      ) : null}
      {withNote === "line" && note !== undefined ? (
        <span
          className="inv-hint held-adjustment-note-line"
          title={note}
          data-testid={testId === undefined ? undefined : `${testId}-note`}
        >
          {`“${note}”`}
        </span>
      ) : null}
    </span>
  );
}
